//frontend/src/components/dashboard/SpecialistManagement.js
import React, { useState, useEffect } from 'react';
import {
   Box,
   Paper,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   Button,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogContentText,
   DialogActions,
   Chip,
   IconButton,
   Typography,
   Tabs,
   Tab,
   Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Edit as EditIcon, Person as PersonIcon, PhotoCamera } from '@mui/icons-material';
import axiosInstance from '../../utils/axios';
import ServicesDialog from './ServicesDialog';
import ScheduleDialog from './ScheduleDialog';
import RoleDialog from './RoleDialog';
import ImageUploadDialog from './ImageUploadDialog';
import { useTranslation } from 'react-i18next';

export default function SpecialistManagement() {
   const { t } = useTranslation();
   const [specialists, setSpecialists] = useState([]);
   const [pendingSpecialists, setPendingSpecialists] = useState([]);
   const [services, setServices] = useState([]);
   const [selectedSpecialist, setSelectedSpecialist] = useState(null);
   const [openServicesDialog, setOpenServicesDialog] = useState(false);
   const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
   const [tabValue, setTabValue] = useState(0);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [specialistToDelete, setSpecialistToDelete] = useState(null);
   const [roleDialogOpen, setRoleDialogOpen] = useState(false);
   const [selectedSpecialistForRole, setSelectedSpecialistForRole] = useState(null);
   const [userRole, setUserRole] = useState(null);
   const [openImageDialog, setOpenImageDialog] = useState(false);

   useEffect(() => {
       fetchSpecialists();
       fetchUserRole();
       fetchServices();
   }, []);

   const fetchUserRole = async () => {
       try {
           const response = await axiosInstance.get('/auth/profile');
           setUserRole(response.data.role);
           if (['admin', 'superadmin'].includes(response.data.role)) {
               fetchPendingSpecialists();
           }
       } catch (error) {
           console.error('Error fetching user role:', error);
       }
   };

   const fetchSpecialists = async () => {
       try {
           const response = await axiosInstance.get('/specialists');
           setSpecialists(response.data);
       } catch (error) {
           console.error('Error fetching specialists:', error);
       }
   };

   const fetchPendingSpecialists = async () => {
       try {
           const response = await axiosInstance.get('/auth/pending-specialists');
           setPendingSpecialists(response.data);
       } catch (error) {
           console.error('Error fetching pending specialists:', error);
       }
   };

   const fetchServices = async () => {
       try {
           const response = await axiosInstance.get('/services');
           setServices(response.data);
       } catch (error) {
           console.error('Error fetching services:', error);
       }
   };

   const handleApproveSpecialist = async (specialistId) => {
       try {
           await axiosInstance.patch(`/auth/specialist/${specialistId}`, { status: 'approved' });
           fetchPendingSpecialists();
           fetchSpecialists();
       } catch (error) {
           console.error('Error approving specialist:', error);
       }
   };

   const handleRejectSpecialist = async (specialistId) => {
       try {
           await axiosInstance.patch(`/auth/specialist/${specialistId}`, { status: 'rejected' });
           fetchPendingSpecialists();
       } catch (error) {
           console.error('Error rejecting specialist:', error);
       }
   };

   const handleUpdateServices = async (selectedServices) => {
       try {
           await axiosInstance.patch(`/specialists/${selectedSpecialist._id}/services`, {
               services: selectedServices
           });
           fetchSpecialists();
           setOpenServicesDialog(false);
       } catch (error) {
           console.error('Error updating services:', error);
       }
   };

   const handleImageUpload = async (specialistId, file) => {
    try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        
        const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });

        const base64 = await base64Promise;
        
        await axiosInstance.post(`/specialists/${specialistId}/image`, { 
            image: base64 
        });
        
        await fetchSpecialists();
    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};

const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

   const handleRoleChange = async (newRole) => {
       try {
           await axiosInstance.patch(`/specialists/${selectedSpecialistForRole._id}/role`, {
               role: newRole
           });
           fetchSpecialists();
           setRoleDialogOpen(false);
           setSelectedSpecialistForRole(null);
       } catch (error) {
           console.error('Error updating role:', error);
       }
   };

   const handleUpdateSchedule = async (schedule) => {
       try {
           await axiosInstance.patch(`/specialists/${selectedSpecialist._id}/schedule`, {
               schedule: schedule
           });
           fetchSpecialists();
           setOpenScheduleDialog(false);
       } catch (error) {
           console.error('Error updating schedule:', error);
       }
   };

   const handleDeleteClick = (specialist) => {
       setSpecialistToDelete(specialist);
       setDeleteDialogOpen(true);
   };

   const handleDeleteConfirm = async () => {
       try {
           await axiosInstance.delete(`/specialists/${specialistToDelete._id}`);
           fetchSpecialists();
           setDeleteDialogOpen(false);
           setSpecialistToDelete(null);
       } catch (error) {
           console.error('Error deleting specialist:', error);
       }
   };

   const handleDeleteCancel = () => {
       setDeleteDialogOpen(false);
       setSpecialistToDelete(null);
   };

   const PendingSpecialistsTable = () => (
       <TableContainer component={Paper}>
           <Table>
               <TableHead>
                   <TableRow>
                       <TableCell>{t('common.name')}</TableCell>
                       <TableCell>{t('common.email')}</TableCell>
                       <TableCell>{t('common.phone')}</TableCell>
                       <TableCell>{t('common.actions')}</TableCell>
                   </TableRow>
               </TableHead>
               <TableBody>
                   {pendingSpecialists.map((specialist) => (
                       <TableRow key={specialist._id}>
                           <TableCell>{specialist.name}</TableCell>
                           <TableCell>{specialist.email}</TableCell>
                           <TableCell>{specialist.phone}</TableCell>
                           <TableCell>
                               <Button
                                   variant="contained"
                                   color="success"
                                   size="small"
                                   onClick={() => handleApproveSpecialist(specialist._id)}
                                   sx={{ mr: 1 }}
                               >
                                   {t('common.approve')}
                               </Button>
                               <Button
                                   variant="contained"
                                   color="error"
                                   size="small"
                                   onClick={() => handleRejectSpecialist(specialist._id)}
                               >
                                   {t('common.reject')}
                               </Button>
                           </TableCell>
                       </TableRow>
                   ))}
               </TableBody>
           </Table>
       </TableContainer>
   );

   const ActiveSpecialistsTable = () => (
       <TableContainer component={Paper}>
           <Table>
               <TableHead>
                   <TableRow>
                       <TableCell>{t('common.name')}</TableCell>
                       <TableCell>{t('common.role')}</TableCell>
                       <TableCell>{t('common.services')}</TableCell>
                       <TableCell>{t('common.schedule')}</TableCell>
                       <TableCell>{t('common.phone')}</TableCell>
                       <TableCell>{t('common.email')}</TableCell>
                       <TableCell>{t('common.actions')}</TableCell>
                   </TableRow>
               </TableHead>
               <TableBody>
                   {specialists.map((specialist) => (
                       <TableRow key={specialist._id}>
                           <TableCell>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                   {specialist.image && (
                                       <img 
                                           src={specialist.image} 
                                           alt={specialist.name}
                                           style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                       />
                                   )}
                                   {specialist.name}
                               </Box>
                           </TableCell>
                           <TableCell>
                               {t(`common.${specialist.role}`)}
                           </TableCell>
                           <TableCell>
                               {specialist.role !== 'superadmin' && (
                                   <Box>
                                       <Button
                                           size="small"
                                           variant="outlined"
                                           onClick={() => {
                                               setSelectedSpecialist(specialist);
                                               setOpenServicesDialog(true);
                                           }}
                                           sx={{ mb: 1 }}
                                       >
                                           {t('common.editservices')}
                                       </Button>
                                       <Box>
                                           {specialist.services?.map((service) => (
                                               <Chip
                                                   key={service}
                                                   label={service}
                                                   size="small"
                                                   sx={{ m: 0.5 }}
                                               />
                                           ))}
                                       </Box>
                                   </Box>
                               )}
                           </TableCell>
                           <TableCell>
                               {specialist.role !== 'superadmin' && (
                                   <Button
                                       size="small"
                                       variant="outlined"
                                       onClick={() => {
                                           setSelectedSpecialist(specialist);
                                           setOpenScheduleDialog(true);
                                       }}
                                   >
                                       {t('common.setschedule')}
                                   </Button>
                               )}
                           </TableCell>
                           <TableCell>{specialist.phone}</TableCell>
                           <TableCell>{specialist.email}</TableCell>
                           <TableCell>
                               {specialist.role !== 'superadmin' && (
                                   <Box sx={{ display: 'flex', gap: 1 }}>
                                       <IconButton
                                           size="small"
                                           onClick={() => {
                                               setSelectedSpecialist(specialist);
                                               setOpenImageDialog(true);
                                           }}
                                       >
                                           <PhotoCamera fontSize="small" />
                                       </IconButton>
                                       <Tooltip title={t('common.editRole')}>
                                           <IconButton
                                               size="small"
                                               onClick={() => {
                                                   setSelectedSpecialistForRole(specialist);
                                                   setRoleDialogOpen(true);
                                               }}
                                           >
                                               <PersonIcon fontSize="small" />
                                           </IconButton>
                                       </Tooltip>
                                       <Tooltip title={t('common.delete')}>
                                           <IconButton
                                               color="error"
                                               size="small"
                                               onClick={() => handleDeleteClick(specialist)}
                                           >
                                               <DeleteIcon fontSize="small" />
                                           </IconButton>
                                       </Tooltip>
                                   </Box>
                               )}
                           </TableCell>
                       </TableRow>
                   ))}
               </TableBody>
           </Table>
       </TableContainer>
   );

   return (
       <Box>
           <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
               <Tab label={t('common.activeSpecialists')} />
               {['admin', 'superadmin'].includes(userRole) && (
                   <Tab label={t('common.pendingApprovals')} />
               )}
           </Tabs>

           {tabValue === 0 ? <ActiveSpecialistsTable /> : (
               ['admin', 'superadmin'].includes(userRole) && <PendingSpecialistsTable />
           )}

           <ServicesDialog
               open={openServicesDialog}
               onClose={() => setOpenServicesDialog(false)}
               specialist={selectedSpecialist}
               services={services}
               onSave={handleUpdateServices}
           />

           <ScheduleDialog
               open={openScheduleDialog}
               onClose={() => setOpenScheduleDialog(false)}
               specialist={selectedSpecialist}
               onSave={handleUpdateSchedule}
           />

           <RoleDialog
               open={roleDialogOpen}
               onClose={() => {
                   setRoleDialogOpen(false);
                   setSelectedSpecialistForRole(null);
               }}
               specialist={selectedSpecialistForRole}
               onSave={handleRoleChange}
           />

           <Dialog
               open={deleteDialogOpen}
               onClose={handleDeleteCancel}
           >
               <DialogTitle>{t('common.confirmDeletion')}</DialogTitle>
               <DialogContent>
                   <DialogContentText>
                       {t('common.deleteSpecialistConfirm', { name: specialistToDelete?.name })}
                   </DialogContentText>
               </DialogContent>
               <DialogActions>
                   <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
                   <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                       {t('common.delete')}
                   </Button>
               </DialogActions>
           </Dialog>

           <ImageUploadDialog
               open={openImageDialog}
               onClose={() => setOpenImageDialog(false)}
               specialist={selectedSpecialist}
               onUpload={handleImageUpload}
           />
       </Box>
   );
}